import type { SSHKeysReduxState } from "ee/reducers/sshKeysReducer";

/**
 * State type that has sshKeys reducer
 * This is compatible with both standalone SSHKeysRootState and AppState
 */
interface StateWithSSHKeys {
  sshKeys: SSHKeysReduxState;
}

/**
 * Select the entire SSH keys state
 */
export const selectSSHKeysState = (state: StateWithSSHKeys) => state.sshKeys;

/**
 * Select SSH keys list state
 */
export const selectSSHKeys = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).keys;

/**
 * Select SSH keys list value
 */
export const selectSSHKeysList = (state: StateWithSSHKeys) =>
  selectSSHKeys(state).value;

/**
 * Select SSH keys loading state
 */
export const selectSSHKeysLoading = (state: StateWithSSHKeys) =>
  selectSSHKeys(state).loading;

/**
 * Select SSH keys error
 */
export const selectSSHKeysError = (state: StateWithSSHKeys) =>
  selectSSHKeys(state).error;

/**
 * Select create SSH key state
 */
export const selectCreateSSHKeyState = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).createSSHKey;

/**
 * Select create SSH key loading state
 */
export const selectCreateSSHKeyLoading = (state: StateWithSSHKeys) =>
  selectCreateSSHKeyState(state).loading;

/**
 * Select create SSH key error
 */
export const selectCreateSSHKeyError = (state: StateWithSSHKeys) =>
  selectCreateSSHKeyState(state).error;

/**
 * Select delete SSH key state
 */
export const selectDeleteSSHKeyState = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).deleteSSHKey;

/**
 * Select delete SSH key loading state
 */
export const selectDeleteSSHKeyLoading = (state: StateWithSSHKeys) =>
  selectDeleteSSHKeyState(state).loading;

/**
 * Select delete SSH key error
 */
export const selectDeleteSSHKeyError = (state: StateWithSSHKeys) =>
  selectDeleteSSHKeyState(state).error;

/**
 * Select create modal open state
 */
export const selectIsCreateModalOpen = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).isCreateModalOpen;

/**
 * Select delete modal open state
 */
export const selectIsDeleteModalOpen = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).isDeleteModalOpen;

/**
 * Select selected key ID
 */
export const selectSelectedKeyId = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).selectedKeyId;

/**
 * Select the currently selected SSH key
 */
export const selectSelectedKey = (state: StateWithSSHKeys) => {
  const selectedKeyId = selectSelectedKeyId(state);
  const keys = selectSSHKeysList(state);

  if (!selectedKeyId || !keys) return null;

  return keys.find((key) => key.id === selectedKeyId) ?? null;
};

/**
 * Select owned SSH keys (where user email matches key email)
 */
export const selectOwnedSSHKeys = (
  state: StateWithSSHKeys,
  userEmail: string,
) => {
  const keys = selectSSHKeysList(state);

  if (!keys) return null;

  return keys.filter((key) => key.email === userEmail);
};

/**
 * Select shared SSH keys (where user email is in sharedEmails)
 */
export const selectSharedSSHKeys = (
  state: StateWithSSHKeys,
  userEmail: string,
) => {
  const keys = selectSSHKeysList(state);

  if (!keys) return null;

  return keys.filter(
    (key) => key.email !== userEmail && key.sharedEmails.includes(userEmail),
  );
};

/**
 * Select export SSH key state
 */
export const selectExportSSHKeyState = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).exportSSHKey;

/**
 * Select export SSH key loading state
 */
export const selectExportSSHKeyLoading = (state: StateWithSSHKeys) =>
  selectExportSSHKeyState(state).loading;

/**
 * Select the ID of the key currently being exported
 */
export const selectExportingKeyId = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).exportingKeyId;

/**
 * Select import SSH key state
 */
export const selectImportSSHKeyState = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).importSSHKey;

/**
 * Select import SSH key loading state
 */
export const selectImportSSHKeyLoading = (state: StateWithSSHKeys) =>
  selectImportSSHKeyState(state).loading;

/**
 * Select import SSH key error
 */
export const selectImportSSHKeyError = (state: StateWithSSHKeys) =>
  selectImportSSHKeyState(state).error;

/**
 * Select share SSH key state
 */
export const selectShareSSHKeyState = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).shareSSHKey;

/**
 * Select share SSH key loading state
 */
export const selectShareSSHKeyLoading = (state: StateWithSSHKeys) =>
  selectShareSSHKeyState(state).loading;

/**
 * Select share SSH key error
 */
export const selectShareSSHKeyError = (state: StateWithSSHKeys) =>
  selectShareSSHKeyState(state).error;

/**
 * Select revoke SSH key state
 */
export const selectRevokeSSHKeyState = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).revokeSSHKey;

/**
 * Select revoke SSH key loading state
 */
export const selectRevokeSSHKeyLoading = (state: StateWithSSHKeys) =>
  selectRevokeSSHKeyState(state).loading;

/**
 * Select share modal open state
 */
export const selectIsShareModalOpen = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).isShareModalOpen;

/**
 * Select import modal open state
 */
export const selectIsImportModalOpen = (state: StateWithSSHKeys) =>
  selectSSHKeysState(state).isImportModalOpen;
